import { useContext, useEffect, useState } from "react";

import { MediaLoadPriorityContext } from "@is/contexts";
import { MediaManager } from "@is/services/MediaManager";
import { CacheEntryPriority } from "@is/services/MemoryCache";

type State = {
  src: string;
  isLoading: boolean;
};

type Options = {
  placeholderSrc?: string;
  cachePriority?: CacheEntryPriority;
};

// all supplied load priorities will be offset with this
const BASE_LOAD_PRIORITY = 1000;

export default function useMedia(src: string, options?: Options): State {
  const loadPriority = useContext(MediaLoadPriorityContext);

  const existingMedia = MediaManager.instance.get(src);
  const [state, setState] = useState<State>({
    src: existingMedia ? existingMedia.src : options?.placeholderSrc,
    isLoading: !existingMedia,
  });

  // we want the load priorities to be higher than preload priorities
  const adjustedLoadPriority = BASE_LOAD_PRIORITY + loadPriority;

  if (existingMedia && state.src !== existingMedia.src) {
    setState({
      src: existingMedia.src,
      isLoading: false,
    });
  }

  useEffect(() => {
    let release: () => void;
    let cancel: () => void;

    if (existingMedia) {
      release = existingMedia.release;
    } else {
      const loadMediaItem = {
        url: src,
        cachePriority: options?.cachePriority ?? CacheEntryPriority.NORMAL,
      };

      const media = MediaManager.instance.load(
        loadMediaItem,
        options?.placeholderSrc,
        adjustedLoadPriority
      );

      release = media.release;
      cancel = media.cancel;

      media.srcPromise
        .then((src) => {
          setState({
            src,
            isLoading: false,
          });
        })
        .catch((error) => {
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            throw error;
          }
        });
    }

    return () => {
      release();

      if (cancel) {
        cancel();
      }
    };
  }, [
    src,
    loadPriority,
    existingMedia,
    adjustedLoadPriority,
    options?.placeholderSrc,
    options?.cachePriority,
  ]);

  return state;
}
